import React, { useState, useRef, useEffect } from 'react';
import { Location } from '../types/chat';

interface StackedLocationCarouselProps {
  locations: Location[];
  onLocationSelect: (location: Location | null) => void;
  onLocationsUpdate: (locations: Location[]) => void;
  selectedLocation: Location | null;
  visibleCards?: number;
  LocationRecommendation: React.ComponentType<any>;
}

export default function StackedLocationCarousel({ 
  locations = [], 
  onLocationSelect = () => {}, 
  onLocationsUpdate = () => {},
  selectedLocation = null,
  visibleCards = 5,
  LocationRecommendation
}: StackedLocationCarouselProps) {
  const [showAll, setShowAll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const transformTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [scrollState, setScrollState] = useState<'start' | 'middle' | 'end'>('start');
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [transform, setTransform] = useState(0); // For spring effect
  
  const hasMore = locations.length > visibleCards;
  const displayedLocations = showAll ? locations : locations.slice(0, visibleCards);

  // Update scroll state based on position
  const updateScrollState = () => {
    if (!scrollContainerRef.current) return;
    
    requestAnimationFrame(() => {
      if (!scrollContainerRef.current) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const maxScroll = scrollWidth - clientWidth;
      
      if (scrollLeft <= 5) { // Small threshold
        setScrollState('start');
      } else if (scrollLeft >= maxScroll - 5) {
        setScrollState('end');
      } else {
        setScrollState('middle');
      }
    });
  };

  // Handle scroll with debounce
  const handleScroll = () => {
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set new timeout to update scroll state
    scrollTimeoutRef.current = setTimeout(() => {
      updateScrollState();
    }, 50); // Small delay to prevent rapid updates
  };

  // Add wheel event listener manually with non-passive option
 /*  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle horizontal scrolling
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;
      
      // Clear any existing transform timeout
      if (transformTimeoutRef.current) {
        clearTimeout(transformTimeoutRef.current);
      }
      
      // At start boundary - trying to scroll left
      if (scrollLeft <= 5 && e.deltaX < 0) {
        e.preventDefault();
        const force = Math.min(Math.abs(e.deltaX) * 0.5, 80);
        setTransform(force);
        transformTimeoutRef.current = setTimeout(() => setTransform(0), 400);
      }
      // At end boundary - trying to scroll right
      else if (scrollLeft >= maxScroll - 5 && e.deltaX > 0) {
        e.preventDefault();
        const force = Math.max(-Math.abs(e.deltaX) * 0.5, -80);
        setTransform(force);
        transformTimeoutRef.current = setTimeout(() => setTransform(0), 400);
      }
    };

    // Add non-passive wheel listener
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (transformTimeoutRef.current) {
        clearTimeout(transformTimeoutRef.current);
      }
    };
  }, []); */ // Empty deps, we don't need to re-add listener

  // Mouse/Touch drag handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
    setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
    
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grabbing';
      scrollContainerRef.current.style.userSelect = 'none';
    }
  };

  /* const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const walk = (startX - clientX) * 1.5; // Multiply for faster scroll
    
    scrollContainerRef.current.scrollLeft = scrollLeft + walk;
  }; */

  /* const handleDragEnd = () => {
    if (!scrollContainerRef.current) return;
    
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = 'auto';
    scrollContainerRef.current.style.userSelect = '';
  };
 */
  // Scroll button handlers
  const scrollLeftBtn = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRightBtn = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Initialize scroll state with delay to prevent shifting
  useEffect(() => {
    updateScrollState();
  }, [displayedLocations]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (transformTimeoutRef.current) {
        clearTimeout(transformTimeoutRef.current);
      }
    };
  }, []);

  // Global mouse up handler
  useEffect(() => {
    const handleGlobalMouseUp = () => handleDragEnd();
    const handleGlobalTouchEnd = () => handleDragEnd();
    
    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchend', handleGlobalTouchEnd);
      return () => {
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging]);

  return (
    <div style={{ 
      width: '100%',
      marginTop: 'var(--spacing-xl)',
      overflow: 'visible'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--spacing-lg)',
        paddingRight: '10px'
      }}>
        <button
          onClick={() => {
            onLocationSelect(null);
            onLocationsUpdate(locations);
            setTimeout(() => {
              window.dispatchEvent(new Event('map-reset-view'));
            }, 50);
          }}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
          style={{ marginRight: 'auto' }}>
          <span>📍</span>
          Show locations
        </button>
      </div>

      {/* Carousel container with gradients */}
      <div style={{ 
        position: 'relative',
        overflowX: 'visible', // Hide horizontal overflow for gradients
        overflowY: 'visible' // Allow vertical overflow for shadows
      }}>
        {/* Left gradient - only on image area */}
        <div 
          className="pointer-events-none"
          style={{ 
            position: 'absolute',
            left: 0,
            top: 0,
            height: '200px', // Partial coverage for portrait cards
            width: '30px',
            background: 'linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 5,
            opacity: scrollState === 'start' ? 0 : 1,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
          }}
        />
        
        {/* Right gradient - only on image area */}
        <div 
          className="pointer-events-none"
          style={{ 
            position: 'absolute',
            right: 0,
            top: 0,
            height: '200px', // Partial coverage for portrait cards
            width: '30px',
            background: 'linear-gradient(to left, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 5,
            opacity: scrollState === 'end' ? 0 : 1,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
          }}
        />

        {/* Scroll buttons */}
        {displayedLocations.length > 3 && scrollState !== 'start' && (
          <button
            onClick={scrollLeftBtn}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all opacity-80 hover:opacity-100"
          >
            <span>‹</span>
          </button>
        )}

        {displayedLocations.length > 3 && scrollState !== 'end' && (
          <button
            onClick={scrollRightBtn}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all opacity-80 hover:opacity-100"
          >
            <span>›</span>
          </button>
        )}

        {/* Location cards */}
        <div 
          ref={scrollContainerRef}
          onScroll={(e) => {
            handleScroll();
            // Reset transform on actual scroll
            if (transform !== 0) setTransform(0);
          }}
          /* onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseLeave={handleDragEnd} */ // End drag if mouse leaves
          style={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: 'var(--spacing-md)',
            width: '100%',
            overflowX: 'scroll',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            //overflow: 'visible',  // ADD THIS
            height: '300px',
            paddingBottom: '30px',  // Change this
            paddingTop: '0px',      // Change this
            paddingLeft: '4px',
            paddingRight: '4px',
            cursor: isDragging ? 'grabbing' : 'auto',
            WebkitOverflowScrolling: 'touch',
            // Spring transform with more noticeable bounce
            //transform: `translateX(${transform}px)`,
            //transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.35, 0.8, 0.35, 1)',
          }}
          className="hide-scrollbar"
        >
          {displayedLocations.map((location, index) => (
            <div 
              key={location.id}
              style={{
                flexShrink: 0,
                width: 'var(--location-card-width, 170px)', // Fixed width for cards
              }}
              onMouseDown={(e) => {
                // Prevent drag if clicking on interactive elements
                const target = e.target as HTMLElement;
                if (target.closest('button') || target.closest('a')) {
                  e.stopPropagation();
                }
              }}
            >
              <LocationRecommendation
                location={location}
                index={index}
                isVisible={true}
                onClick={() => {
                  // Only trigger click if not dragging
                  if (!isDragging) {
                    onLocationSelect(location);
                  }
                }}
                onHoverEnter={(locationId: string) => {
                  if (!isDragging) {
                    window.dispatchEvent(new CustomEvent('tripper-location-hover', {
                      detail: { locationId }
                    }));
                  }
                }}
                onHoverLeave={() => {
                  if (!isDragging) {
                    window.dispatchEvent(new CustomEvent('tripper-location-hover-end'));
                  }
                }}
                isActive={selectedLocation?.id === location.id}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Show more/less button */}
      {hasMore && (
        <div style={{
          marginTop: 'var(--spacing-lg)',
          textAlign: 'center'
        }}>
          <button
            onClick={() => setShowAll(!showAll)}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            {showAll 
              ? `Show less` 
              : `Show all ${locations.length} locations`
            }
          </button>
        </div>
      )}
    </div>
  );
}